import { jsx as _jsx } from "react/jsx-runtime";
import { intercept } from "@ispb/react-hooks";
import { useParams, useSearchParams as useSearchParamsFromRouter } from "react-router-dom";
import { callOrGet } from "value-or-factory";
import ErrorPage from "./components/ErrorPage.js";
import { Either } from "./namespaces/Either.js";
import { Mop } from "./namespaces/Mop.js";
import { Ramda } from "./namespaces/Ramda.js";
/**
 * Higher order component that uses a mapper, renders an error page on failure.
 */
export function mapped(factory) {
    return intercept((props) => {
        const options = factory(props);
        const result = Mop.execOrSingleErrorMessage({ ...useParams() }, options.map);
        if (Either.isRight(result)) {
            return {
                type: "transform",
                props: {
                    ...options.passthrough,
                    ...result.right,
                }
            };
        }
        else {
            return {
                type: "replace",
                element: _jsx(ErrorPage, { title: "URL Parameter Error", children: result.left })
            };
        }
    });
}
/**
 * Higher order component that uses a mapper, renders an error page on failure.
 */
export function mappedSearch(mapper) {
    return intercept((props) => {
        const result = Mop.execOrSingleErrorMessage(useSearchParams()[0], mapper);
        if (Either.isLeft(result)) {
            return {
                type: "replace",
                element: _jsx(ErrorPage, { title: "URL Parameter Error", children: result.left })
            };
        }
        else {
            return {
                type: "transform",
                props: {
                    ...props,
                    ...result.right,
                }
            };
        }
    });
}
export function useSearchParams(options = {}) {
    const [params, setParams] = useSearchParamsFromRouter();
    const value = Object.fromEntries([...params.entries()]);
    const setValue = (state) => {
        setParams(prev => {
            return Ramda.pickBy(Ramda.isNotNil, callOrGet(state, Object.fromEntries([...prev.entries()])));
        }, options);
    };
    return [value, setValue];
}
export function useSearchParam(key, options = {}) {
    const [params, setParams] = useSearchParamsFromRouter();
    const param = params.get(key) ?? undefined;
    const setParam = (state) => {
        setParams(prev => {
            return Ramda.pickBy(Ramda.isNotNil, {
                ...Object.fromEntries([...prev.entries()]),
                [key]: callOrGet(state, prev.get(key) ?? undefined)
            });
        }, options);
    };
    return [param, setParam];
}
